import { FieldValueObject } from '../components/cards/product-card/utils/field/field';
import { CodeValue } from '../../shared/models/code-value';

import { ProductConfiguration } from './product-configuration';

export interface BocOpt {
	soldTo?: string | undefined;
	shipTo?: string | undefined;
	endUser?: string | undefined;
}

// OFFER MODELS
type OfferStatus = 'DRAFT' | 'REQUESTED' | 'OFFERED' | 'ACCEPTED' | 'CANCELED';

export interface Offer {
	header: OfferHeader;
	products?: OfferProduct[] | undefined;
}

export interface OfferHeaderId {
	id?: number | undefined;
	externalId?: string | undefined;
}

interface OfferHeader extends BocOpt, OfferHeaderId {
	rfqRef?: string | undefined;
	referenceAm?: string | undefined;
	periodStartMonth?: string | undefined;
	periodEndMonth?: string | undefined;
	idd?: string | undefined;
	tmode?: string | undefined;
	incoterm?: string | undefined;
	pricingUnit?: string | undefined;
	currency?: string | undefined;
	comment?: string | undefined;
	modificationDate?: Date | undefined;
	manager?: string | undefined;
	assistant?: string | undefined;
	technician?: string | undefined;
	weightAndInvoicing?: string | undefined;
	paymentTerms?: string | undefined;
	validityDate?: Date | undefined;
}

interface OfferProductId {
	id?: number | undefined;
	externalId?: string | undefined;
}

export interface ProductRef {
	description: ProductShortDescriptionRef;
	ufoProductRef?: number | undefined;
}

export interface OfferProduct extends OfferProductId, ProductRef {
	description: ProductShortDescription;
	referenceAm?: string | undefined;
	quantity?: number | undefined;
	rdd?: string | undefined;
	deliveryMill?: string | undefined;
	price?: number | undefined;
	offerStatus: OfferStatus;
	feasibilityStatus: FeasibilityStatus;
	characteristics?: ProductA2c | undefined;
}

interface ProductShortDescriptionRef {
	productRequestId?: string | undefined;
	productRef?: string | undefined;
	configurationRef?: string | undefined;
}

interface ProductShortDescription extends ProductShortDescriptionRef {
	customerArticle?: string | undefined;
	family: ProductFamily;
	steelGrade: string;
	dimension: ProductDimension;
}

interface ProductFamily {
	category: string;
	categoryDescription: string;
	subCategory: string;
	subCategoryDescription: string;
	form: string;
	formDescription: string;
	code: string;
	customerTemplate: Template[];
}

interface Template {
	customerTemplateNumber?: number;
	productType?: string;
}

interface ProductDimension {
	thickness: ProductDimensionValue;
	width: ProductDimensionValue;
	length?: ProductDimensionValue | undefined;
}

interface ProductDimensionValue {
	value: number;
	unit: string;
}

// PRODUCT MODELS
export type FeasibilityStatus = 'YES' | 'PENDING' | 'NO';

export const PRODUCT_TYPE_A2C = 'a2c';

export type ProductType = 'a2c' | 'rtpc' | 'ufo';

interface ProductA2c {
	[key: string]: string | undefined;
	a2citemNumber: string;
	a2citemRelease: string;
	configurationNumber?: string;
	productCategoryCode: string;
	productSubCategoryCode: string;
	productFormCode: string;
	commercialSteelGradeID: string;
	thicknessValue: string;
	thicknessUnit: string;
	widthValue: string;
	widthUnit: string;
	commercialSteelGradeIDDescription: string;
	type: ProductType;
	customerTemplateNumber: string;
	customerTemplateReference: string;
	userPartReference: string;
	productType: string;
	commercialMetallicCoatingIDDescription: string;
	dimension: string;
	surfaceAspect: string;
	organicCoatingDataSheet: string;
	colourTop: string;
	removableFilm: string;
	organicCoatingDataSheetBottom: string;
	colorBottom: string;
	lengthValue?: string | undefined;
	lengthUnit?: string | undefined;
}

interface OrderDetail {
	orderNumber: string;
	orderItemNumber: string;
	productReference: string;
	productVersion: number;
	rdd: string;
	quantityInTon: number;
}

interface OrderSynthesis {
	maxProductVersion: number;
	latestRdd: string;
	orderCount: number;
	sumQuantityInTon: number;
	details: OrderDetail[];
}

interface ProductConf {
	status: FeasibilityStatus;
	productRef: string;
	ufoProductRef: number;
	configurationRef: string;
}

interface ProposedProduct extends ProductRef {
	description: ProductShortDescription;
	ufoProductRef?: number | undefined;
	characteristics: ProductA2c;
	orderSynthesis?: OrderSynthesis | undefined;
	configurations?: ProductConf[] | undefined;
}

export interface TemplateSearchResponse {
	results: TemplateDescription[];
	count: number;
	perfectMatch: boolean;
	translatedConf: ProductConfiguration;
}

export interface TemplateDescription {
	/** 64 bits */
	templateNumber: number;
	templateRelease: number;
	properties: Record<string, TemplateVariable>;
	customerTemplateReference: string;
}

export interface TemplateVariable {
	type: 'string' | 'integer' | 'number' | 'array';
	properties?: Record<string, TemplateVariable>;
	path: string;
}

export interface TemplateVariableString extends TemplateVariable {
	maxLength: number;
	free: boolean;
}

export interface TemplateVariableNumber extends TemplateVariable {
	/** e.g. 9999 or 999.99 */
	maximum: string;
}

export interface TemplateVariableArray extends TemplateVariable {
	/** only string, integer or number */
	properties: Record<string, TemplateVariable>;
	maxItems: number;
}

export interface TemplateVariableValues {
	orderBookValues: CodeValue[];
	otherValues: CodeValue[];
}

// UFO PRODUCT MODELS

export interface UfoProductSubmissionRequest {
	templateRef: string;
	soldTo: string;
	shipTo: string | undefined;
	endUser: string | undefined;
	description: ProductShortDescription;
	configuration: FieldValueObject;
}

export interface UfoProductSubmissionResponse {
	ok: boolean;
	product?: ProposedProduct | undefined;
	errors?: string[] | undefined;
}

export interface UfoProductSimulationRequest {
	templateReference: string;
	configuration: ProductConfiguration | FieldValueObject;
}

export interface UfoProductSimulationResponse {
	configuration?: ProductConfiguration | undefined;
	errors?: string[] | undefined;
}
